import React, { useState, useContext, useMemo, useEffect, JSX } from "react";

import {
  NavItem,
  STATIC_END_NAV_ITEMS,
  buildNavItemsFromCollections,
} from "utils/buildNavItems";
import { ModalContext } from "context/modal";
import { UserContext } from "context/user";
import { CartContext } from "context/cart";
import { endpoints, headerPromotion } from "config";
import { AlgoliaSearch } from "@pepdirect/v3/ui/AlgoliaSearch";
import {
  useAdvancedVariantSearchLazyQuery,
  useCollectionsQuery,
  VariantFragment,
} from "services/graphql/generated";

import { useScreenSize } from "@pepdirect/v3/hooks/useScreenSize";
import { useRouterWithRedirect } from "hooks/useRouterWithRedirect";
import {
  NavHeader as PepNavHeader,
  CartProps,
  LocationProps,
  LogoProps,
  NavLinksProps,
  UserProps,
  SearchBarProps,
} from "@pepdirect/v3/ui/NavHeader";
import { logSignUp, logSignIn } from "@pepdirect/v3/helpers/analyticsLogger";
import { LOGIN_LOCATION_ENUM } from "@pepdirect/v3/types";
import { ZipCodeContext } from "context/zipCode";
import { useGenericOnClickRouter } from "hooks/useGenericOnClickRouter";
import { convertFromCentsToDollars } from "@pepdirect/v3/helpers/currency";
import { AppContext } from "context/app";

export function NavHeader(): JSX.Element {
  const { open } = useContext(ModalContext);
  const { tenant } = useContext(AppContext);
  const { currentUserId, logout } = useContext(UserContext);
  const { getSize } = useContext(CartContext);
  const { zipCode } = useContext(ZipCodeContext);
  const size = getSize();
  const { pushPreserveRedirect } = useRouterWithRedirect();
  const { route } = useGenericOnClickRouter();
  const headerPromotionText = useMemo(() => {
    return `Free Shipping Over ${convertFromCentsToDollars(
      tenant?.shippingConfig.freeShippingThreshold || 0,
      "$0,0"
    )}`;
  }, [tenant?.shippingConfig.freeShippingThreshold]);

  const { isMobile } = useScreenSize();

  const [headerKey, setHeaderKey] = useState("h");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<VariantFragment[]>([]);

  // Use the GraphQL query hook
  const [executeSearch, { data, loading, error }] =
    useAdvancedVariantSearchLazyQuery();
  // Update search results when data changes
  useEffect(() => {
    if (data?.advancedVariantSearch) {
      const results = data.advancedVariantSearch
        .filter(
          (result): result is NonNullable<typeof result> => result !== null
        )
        .filter(
          (result) => result.variant !== null && result.variant !== undefined
        )
        .map((result) => result.variant as VariantFragment);
      setSearchResults(results);
    }
  }, [data]);

  const goToSignIn = () => {
    if (isMobile) {
      // HACK to reset header, should be removed later
      setHeaderKey(`${headerKey}-1`);
      pushPreserveRedirect("/sign-in");
      logSignIn(LOGIN_LOCATION_ENUM.header);
    } else {
      open("LOGIN_MODAL");
    }
  };

  const goToSignUp = () => {
    if (isMobile) {
      // HACK to reset header, should be removed later
      setHeaderKey(`${headerKey}-1`);
    }
    logSignUp(LOGIN_LOCATION_ENUM.header);
    pushPreserveRedirect("/sign-up");
  };

  const logoutAndResetHeader = () => {
    if (isMobile) {
      // HACK to reset header, should be removed later
      setHeaderKey(`${headerKey}-1`);
    }
    logout();
  };

  const tenantAllowsAlgoliaSearch =
    tenant?.userExperience?.algoliaSearchActive || false;

  const searchData: SearchBarProps = {
    showSearchBar: tenantAllowsAlgoliaSearch,
    placeholderText: ``,
    onSearchClicked: () => setIsSearchOpen(!isSearchOpen),
  };

  const userData: UserProps = {
    isLoggedIn: !!currentUserId,
    accountUrl: `${endpoints.portalFrontend}/v3/dashboard/account/`,
    onLogInClicked: goToSignIn,
    onLogOutClicked: logoutAndResetHeader,
    onSignUpClicked: goToSignUp,
  };

  const location: LocationProps = {
    onClickHandler: () => {
      open("ZIPCODE_MODAL");
    },
    zipCode,
  };

  const logo: LogoProps = {
    logoImgLink: `${endpoints.cdn}/static-images/pantry-shop-icons/light-large-pantry-shop-logo.svg`,
    onClickHandler: (e: React.MouseEvent<HTMLAnchorElement>) => {
      route(e, "/");
    },
  };

  // Fetch collections from Algolia for dynamic navigation
  const { data: collectionsData } = useCollectionsQuery();

  // Build navigation items: dynamic collections + static end items
  const navItems = useMemo((): NavItem[] => {
    const dynamicItems = buildNavItemsFromCollections(
      collectionsData?.collections
    );
    return [...dynamicItems, ...STATIC_END_NAV_ITEMS];
  }, [collectionsData?.collections]);

  const navLinks: NavLinksProps = {
    onClickHandler: (e: React.MouseEvent<HTMLAnchorElement>) => route(e),
    navItems,
  };

  const cart: CartProps = {
    numOfItems: size,
    onClickHandler: (e: React.MouseEvent<HTMLAnchorElement>) =>
      route(e, "/cart"),
  };

  // Handle search execution
  const handleSearch = (searchTerm: string) => {
    executeSearch({
      variables: {
        term: searchTerm,
      },
    });
  };

  return (
    <div>
      <PepNavHeader
        key={headerKey}
        location={location}
        logo={logo}
        navLinks={navLinks}
        promotion={{
          ...headerPromotion,
          promotionText: headerPromotionText,
        }}
        searchBar={searchData}
        cart={cart}
        user={userData}
      />

      {isSearchOpen && currentUserId && (
        <AlgoliaSearch<VariantFragment>
          onCloseSearchClicked={() => setIsSearchOpen(false)}
          onSearch={handleSearch}
          results={searchResults}
          loading={loading}
          error={error?.message}
          formatPrice={convertFromCentsToDollars}
        />
      )}
    </div>
  );
}
